import React, { useRef, useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { useSpreadsheetContext } from '../context/SpreadsheetContext';
import gardenBedLayouts from '../data/gardenBedLayouts.json';
import '../css/svg.css';

const PreviewSVG = ({ setIsLoading: parentSetIsLoading = () => {}, styleOverride }) => {
	const { state } = useAppContext();
	const svgRef = useRef(null);
	const { plantData } = useSpreadsheetContext();
	const [isLoading, setIsLoading] = useState(true);
	const [loadError, setLoadError] = useState(null);

	const S3_HOME_PNG_URL = import.meta.env.VITE_S3_HOME_PNGS;
	const S3_PNG_URL = import.meta.env.VITE_S3_PNG_URL;
	const {bedConfig, facade, houseSize, windows, shape, bedSize = 12, chosenPlants } = state.currentDesign || {};
	const currentLayout = bedConfig ? gardenBedLayouts.find((layout) => layout.id === bedConfig) : null;

	const preloadImages = async () => {
		setLoadError(null);
		const imageUrls = new Set();

		// Add background image
		const backgroundUrl = `${S3_HOME_PNG_URL}/background.png`;
		imageUrls.add(backgroundUrl);

		// Add facade image
		if (facade && facade !== 'none') {
			const facadeUrl = facade.startsWith('fence_')
				? `${S3_HOME_PNG_URL}/${facade}.png`
				: houseSize
				? `${S3_HOME_PNG_URL}/${facade}_${houseSize}.png`
				: '';
			if (facadeUrl) {
				imageUrls.add(facadeUrl);
			}
		}

		// Add windows image
		if (windows && windows !== 'windows_none') {
			const windowsUrl = `${S3_HOME_PNG_URL}/${windows}.png`;
			imageUrls.add(windowsUrl);
		}

		// Add garden bed image
		if (shape && bedSize) {
			const bedUrl = `${S3_HOME_PNG_URL}/${shape}_${bedSize}.png`;
			imageUrls.add(bedUrl);
		}

		// Add plant images
		if (currentLayout && chosenPlants && plantData) {
			chosenPlants.forEach((chosenPlant) => {
				const plantDataItem = plantData.find(
					(plant) => plant.ID === chosenPlant.plantId
				);
				if (plantDataItem?.ImageURL) {
					const plantUrl = `${S3_PNG_URL}/${plantDataItem.ImageURL}.png`;
					imageUrls.add(plantUrl);
				}
			});
		}

		const loadImage = (url) => {
			return new Promise((resolve, reject) => {
				const img = new Image();
				img.onload = () => {
					resolve();
				};
				img.onerror = (error) => {
					console.error('Failed to load image:', url, error);
					reject(new Error(`Failed to load image: ${url}`));
				};
				img.src = url;
			});
		};

		try {
			await Promise.all(Array.from(imageUrls).map(loadImage));
		} catch (error) {
			setLoadError(error.message);
		} finally {
			setIsLoading(false);
			parentSetIsLoading(false);
		}
	};

	useEffect(() => {
		setIsLoading(true);
		preloadImages();
	}, [facade, houseSize, windows, shape, bedSize, chosenPlants, currentLayout, plantData]);

	if (isLoading) {
		return (
			<div className="preview-svg-container loading">
				<div className="loading-spinner" />
			</div>
		);
	}

	if (loadError) {
		return (
			<div className="preview-svg-container error">
				<div className="error-message">
					Error loading images: {loadError}
					<button onClick={() => preloadImages()} className="retry-button">
						Retry
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="preview-svg-container">
			<svg 
				ref={svgRef}
				xmlns='http://www.w3.org/2000/svg' 
				viewBox='0 0 1510 850' 
				preserveAspectRatio="xMidYMid meet"
				style={{ width: '100%', height: 'auto', maxWidth: '2000px', ...styleOverride }}
			>
				{/* Background - Always visible */}
				<image
					href={`${S3_HOME_PNG_URL}/background.png`}
					x='0'
					y='0'
					height='850'
					width='1510'
				/>

				{/* House-related images */}
				{facade && facade !== 'none' && (
					<image
						href={
							facade.startsWith('fence_')
								? `${S3_HOME_PNG_URL}/${facade}.png`
								: houseSize
								? `${S3_HOME_PNG_URL}/${facade}_${houseSize}.png`
								: ''
						}
						x='0'
						y='0'
						width='1510'
						height='850'
					/>
				)}
				{windows && windows !== 'windows_none' && (
					<image
						href={`${S3_HOME_PNG_URL}/${windows}.png`}
						x='0'
						y='0'
						width='1510'
						height='850'
					/>
				)}

				{/* Garden bed-related images */}
				{shape && bedSize && (
					<image
						href={`${S3_HOME_PNG_URL}/${shape}_${bedSize}.png`}
						x='0'
						y='0'
						width='1510'
						height='850'
					/>
				)}

				{/* Render plants if data is available */}
				{currentLayout &&
					chosenPlants?.flatMap((chosenPlant) => {
						if (!plantData) {
							console.warn('Plant data is not available.');
							return [];
						}

						const plantDataItem = plantData.find(
							(plant) => plant.ID === chosenPlant.plantId
						);
						if (!plantDataItem) {
							console.warn(`No data found for plantId: ${chosenPlant.plantId}`);
							return [];
						}

						const imageURL = plantDataItem.ImageURL;

						const layoutPlant = currentLayout.plants.find(
							(plant) => plant.category === chosenPlant.category
						);

						if (!layoutPlant || !imageURL) return [];

						const image = new Image();
						image.src = `${S3_PNG_URL}/${imageURL}.png`;

						const imageHeight = image.height;
						const imageWidth = image.width;

						const scale = 1.3;
						const scaledWidth = imageWidth * scale;
						const scaledHeight = imageHeight * scale;

						return layoutPlant.coordinates.map((coord, index) => {
							return (
								<image
									key={`${chosenPlant.category}-${index}`}
									href={`${S3_PNG_URL}/${imageURL}.png`}
									x={coord.x - scaledWidth / 2}
									y={coord.y - scaledHeight}
									width={scaledWidth}
									height={scaledHeight}
								/>
							);
						});
					})}
			</svg>
		</div>
	);
};

export default PreviewSVG;
